import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, assetUrl, errMsg } from "../../lib/api";
import { fmtDateTime, fmtDate } from "../../lib/ui";

export function useAdminGuard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("tl_token")) { navigate("/admin"); return; }
    api.get("/admin/me").then(() => setReady(true)).catch(() => { localStorage.removeItem("tl_token"); navigate("/admin"); });
  }, [navigate]);
  return ready;
}

export function AdminTop({ children }) {
  const navigate = useNavigate();
  const logout = () => { localStorage.removeItem("tl_token"); navigate("/admin"); };
  return (
    <header className="admin-top">
      <div className="admin-brand"><span className="lb-mark">TL</span> AUCTIONS <em>· Control Room</em></div>
      <div className="admin-top-right">
        {children}
        <a className="btn-mini" href="/" target="_blank" rel="noopener noreferrer">View site ↗</a>
        <button className="btn-mini" onClick={logout} data-testid="logout-btn">Log out</button>
      </div>
    </header>
  );
}

export default function AdminDashboard() {
  const ready = useAdminGuard();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState({ upcoming: [], past: [] });
  const [locations, setLocations] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [notice, setNotice] = useState(null);
  const csvRef = useRef(null);

  const load = useCallback(() => {
    api.get("/auctions").then((r) => setAuctions(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/admin/submissions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  if (!ready) return <div className="loading">Loading…</div>;

  const delAuction = async (id) => {
    if (!window.confirm("Delete this auction?")) return;
    await api.delete(`/admin/auctions/${id}`); setNotice("Auction deleted."); load();
  };
  const delLocation = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    await api.delete(`/admin/locations/${id}`); setNotice("Location deleted."); load();
  };
  const notify = async (id) => {
    if (!window.confirm("Email all subscribers about this auction?")) return;
    try { const { data } = await api.post(`/admin/auctions/${id}/notify`);
      setNotice(data.sent > 0 ? `Alert sent to ${data.sent} subscriber(s).` : "No active subscribers to notify yet.");
    } catch (e) { setNotice(errMsg(e)); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/subscribers/import", fd);
      setNotice(`CSV import: ${data.added} added · ${data.skipped_existing} already on the list · ${data.invalid} invalid row(s) skipped.`);
      load();
    } catch (err) { setNotice(errMsg(err)); }
  };

  const AuctionRow = ({ a, showNotify }) => (
    <tr>
      <td>{assetUrl(a.image) && <img className="thumb" src={assetUrl(a.image)} alt="" />}</td>
      <td><strong>{a.title}</strong>{a.featured && <span className="pill" style={{ marginLeft: 6 }}>Featured</span>}
        <div className="muted">{a.location}</div></td>
      <td className="mono">{fmtDateTime(a.auction_date)}</td>
      <td className="actions">
        {showNotify && <button className="btn-mini" onClick={() => notify(a.id)} data-testid={`notify-auction-${a.id}`}>Alert subs</button>}
        <button className="btn-mini" onClick={() => navigate(`/admin/auction/${a.id}`)} data-testid={`edit-auction-${a.id}`}>Edit</button>
        <button className="btn-mini danger" onClick={() => delAuction(a.id)} data-testid={`delete-auction-${a.id}`}>Delete</button>
      </td>
    </tr>
  );

  return (
    <div className="adm">
      <AdminTop>
        <button className="btn-mini" onClick={() => navigate("/admin/settings")} data-testid="nav-settings">Site settings</button>
      </AdminTop>
      <main className="admin-wrap">
        {notice && <div className="msg ok" data-testid="admin-notice">{notice}</div>}

        <section className="panel" data-testid="panel-auctions">
          <div className="panel-head">
            <h2>Upcoming auctions <span className="count">{auctions.upcoming.length}</span></h2>
            <button className="btn-primary" onClick={() => navigate("/admin/auction/new")} data-testid="add-auction-btn">+ New auction</button>
          </div>
          <table className="grid">
            <thead><tr><th /><th>Auction</th><th>Date &amp; time</th><th /></tr></thead>
            <tbody>
              {auctions.upcoming.length === 0 && <tr><td colSpan={4} className="muted pad">No upcoming auctions yet.</td></tr>}
              {auctions.upcoming.map((a) => <AuctionRow key={a.id} a={a} showNotify />)}
            </tbody>
          </table>
          <h3 className="sub">Past auctions</h3>
          <table className="grid"><tbody>
            {auctions.past.length === 0 && <tr><td colSpan={4} className="muted pad">No past auctions.</td></tr>}
            {auctions.past.map((a) => <AuctionRow key={a.id} a={a} />)}
          </tbody></table>
        </section>

        <section className="panel" data-testid="panel-locations">
          <div className="panel-head">
            <h2>Locations <span className="count">{locations.length}</span></h2>
            <button className="btn-primary" onClick={() => navigate("/admin/location/new")} data-testid="add-location-btn">+ New location</button>
          </div>
          <table className="grid">
            <thead><tr><th>Name</th><th>Address</th><th>Phone</th><th /></tr></thead>
            <tbody>
              {locations.length === 0 && <tr><td colSpan={4} className="muted pad">No locations yet.</td></tr>}
              {locations.map((l) => (
                <tr key={l.id}>
                  <td><strong>{l.name}</strong></td>
                  <td className="muted">{[l.address, l.city, l.state, l.zip].filter(Boolean).join(", ")}</td>
                  <td className="mono">{l.phone}</td>
                  <td className="actions">
                    <button className="btn-mini" onClick={() => navigate(`/admin/location/${l.id}`)} data-testid={`edit-location-${l.id}`}>Edit</button>
                    <button className="btn-mini danger" onClick={() => delLocation(l.id)} data-testid={`delete-location-${l.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="panel" data-testid="panel-subscribers">
          <div className="panel-head"><h2>Alert subscribers <span className="count">{subscribers.length}</span></h2>
            <span>
              <input type="file" accept=".csv,text/csv" ref={csvRef} style={{ display: "none" }} onChange={importCsv} data-testid="subscriber-import-input" />
              <button className="btn-mini" onClick={() => csvRef.current?.click()} data-testid="subscriber-import-btn">Import CSV</button>
            </span>
          </div>
          <table className="grid">
            <thead><tr><th>Email</th><th>Joined</th></tr></thead>
            <tbody>
              {subscribers.length === 0 && <tr><td colSpan={2} className="muted pad">No subscribers yet. Visitors join from the homepage alerts box.</td></tr>}
              {subscribers.map((s) => <tr key={s.id}><td>{s.email}</td><td className="mono">{fmtDate(s.created_at)}</td></tr>)}
            </tbody>
          </table>
          <p className="muted" style={{ padding: "14px 10px 4px" }}>Use the <strong>Alert subs</strong> button on any upcoming auction to email everyone at once.</p>
        </section>

        <section className="panel" data-testid="panel-submissions">
          <div className="panel-head"><h2>Recent form submissions <span className="count">{subs.length}</span></h2></div>
          <table className="grid">
            <thead><tr><th>When</th><th>Type</th><th>From</th><th>Details</th></tr></thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={4} className="muted pad">No submissions yet.</td></tr>}
              {subs.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{fmtDate(s.created_at, "MMM d, h:mma")}</td>
                  <td><span className={"pill " + (s.type === "consignment" ? "amber" : "")}>{s.type}</span></td>
                  <td>{s.name}<div className="muted">{s.email} · {s.phone}</div></td>
                  <td className="muted">{s.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
